import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../../api/client";
import { getErrorMessage } from "../../utils/network";
import { useSyncCooldown, fmtCountdown } from "./useSyncCooldown";
import "./OperatorDevicesPanel.css";

// Folder (relative to apiClient's baseURL) where the nms_*.php files live.
// TODO: change this to wherever you uploaded nms_get_devices.php etc.
const NMS_DIR = "/olt";

const apiError = (err, fallback) =>
  err?.response?.data?.message || getErrorMessage(err, fallback);

const fmtDate = (value) => {
  if (!value) return "--";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const statusTone = (status) => {
  const v = String(status || "").toLowerCase();
  if (v === "up" || v.includes("online")) return "online";
  if (v === "down" || v.includes("offline")) return "offline";
  return "unknown";
};

const syncToastMessage = (name, data) => {
  const parts = [];
  if (Number(data.onus) > 0) parts.push(`${data.online ?? 0}/${data.onus} ONUs online`);
  if (Number(data.macs) > 0) parts.push(`${data.macs} MACs`);
  if (Number(data.ports) > 0) parts.push(`${data.ports} ports`);
  const detail = parts.length ? ` — ${parts.join(", ")}` : "";

  if (data.cached) return `${name}: synced recently, showing that result${detail}`;
  const secs = data.seconds ? ` in ${Number(data.seconds).toFixed(1)}s` : "";
  return `${name} synced${secs}${detail}`;
};

// ---------------------------------------------------------------
// One device card — owns its own sync state + 1-minute cooldown.
// ---------------------------------------------------------------
const DeviceCard = ({ device, phone, opening, onOpenSession, onSynced, syncOnly }) => {
  const [syncing, setSyncing] = useState(false);
  const [cooldown, startCooldown] = useSyncCooldown(`device:${device.device_id}`);

  const name = device.device_name || `Device #${device.device_id}`;
  const tone = statusTone(device.status);
  const isOpening = (kind) =>
    opening && opening.id === device.device_id && opening.kind === kind;

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post(
        `${NMS_DIR}/nms_device_sync.php`,
        { device_id: device.device_id, operator_phone: phone, force: false },
        { timeout: 150000 } // a big OLT can take a while
      );
      if (!data?.success) throw new Error(data?.message || "Sync failed");
      toast.success(syncToastMessage(name, data));
      onSynced();
    } catch (err) {
      toast.error(`${name}: ${apiError(err, "Sync failed")}`);
    } finally {
      setSyncing(false);
      startCooldown(); // hide Sync for 1 minute after every attempt
    }
  };

  const canTerminal = !syncOnly && device.can_terminal;
  const canWeb = !syncOnly && device.can_web;
  const noActions = !device.can_sync && !canTerminal && !canWeb;

  return (
    <div className={`nmsp-card nmsp-card--${tone}`}>
      <div className="nmsp-card-head">
        <span className="nmsp-dot" title={device.status || "unknown"} />
        <div className="nmsp-card-title">
          <strong title={name}>{name}</strong>
          <span className="nmsp-sub">
            {[device.device_ip, device.location, device.model].filter(Boolean).join(" · ") || "--"}
          </span>
        </div>
        <span className="nmsp-type">{String(device.device_type || "device").toUpperCase()}</span>
      </div>

      {device.description && <p className="nmsp-desc">{device.description}</p>}

      <div className="nmsp-stats">
        <div>
          <span>Status</span>
          <strong className={`nmsp-status nmsp-status--${tone}`}>{device.status || "unknown"}</strong>
        </div>
        {device.onus_total > 0 && (
          <div>
            <span>ONUs online</span>
            <strong>
              {device.onus_online}/{device.onus_total}
            </strong>
          </div>
        )}
        {device.ports_total > 0 && (
          <div>
            <span>Ports up</span>
            <strong>
              {device.ports_up}/{device.ports_total}
            </strong>
          </div>
        )}
        <div>
          <span>Last sync</span>
          <strong>
            {fmtDate(device.last_sync)}
            {device.last_sync_by ? <em> by {device.last_sync_by}</em> : null}
          </strong>
        </div>
      </div>

      {device.last_error && <p className="nmsp-error">Last error: {device.last_error}</p>}
      {!device.enabled && <p className="nmsp-note">Disabled on the NMS</p>}

      <div className="nmsp-actions">
        {device.can_sync &&
          (syncing ? (
            <button type="button" className="nmsp-btn nmsp-btn--sync" disabled>
              <span className="nmsp-spinner" aria-hidden="true" /> Syncing…
            </button>
          ) : cooldown > 0 ? (
            <span className="nmsp-wait" title="Sync is paused for 1 minute after each sync">
              Sync again in {fmtCountdown(cooldown)}
            </span>
          ) : (
            <button type="button" className="nmsp-btn nmsp-btn--sync" onClick={handleSync}>
              ⟳ Sync
            </button>
          ))}

        {canTerminal && (
          <button
            type="button"
            className="nmsp-btn nmsp-btn--terminal"
            onClick={() => onOpenSession(device, "terminal")}
            disabled={!!opening}
          >
            {isOpening("terminal") ? (
              <>
                <span className="nmsp-spinner" aria-hidden="true" /> Opening…
              </>
            ) : (
              ">_ Terminal"
            )}
          </button>
        )}

        {canWeb && (
          <button
            type="button"
            className="nmsp-btn nmsp-btn--web"
            onClick={() => onOpenSession(device, "web")}
            disabled={!!opening}
          >
            {isOpening("web") ? (
              <>
                <span className="nmsp-spinner" aria-hidden="true" /> Opening…
              </>
            ) : (
              "Web"
            )}
          </button>
        )}

        {noActions && <span className="nmsp-note">No actions available</span>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------
// Terminal / Web session, shown inline in the same window.
// ---------------------------------------------------------------
const SessionView = ({ session, onBack, onReconnect, reconnecting }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const left = Math.max(0, Math.round((session.expiresAt - now) / 1000));
  const title = session.device.device_name || `Device #${session.device.device_id}`;
  const label = session.kind === "terminal" ? "Terminal" : "Web UI";

  // An https page can't show an http:// iframe (browser blocks mixed content).
  const blocked =
    window.location.protocol === "https:" && /^http:/i.test(session.url);

  return (
    <>
      <div className="nmsp-bar">
        <button type="button" className="nmsp-back" onClick={onBack}>
          ← Devices
        </button>
        <div className="nmsp-bar-title">
          <strong title={title}>{title}</strong>
          <span className="nmsp-chip">{label}</span>
          <span className={`nmsp-chip nmsp-ttl ${left === 0 ? "is-expired" : ""}`}>
            {left > 0 ? `Link valid ${fmtCountdown(left)}` : "Link expired"}
          </span>
        </div>
        <div className="nmsp-bar-tools">
          <button type="button" className="nmsp-btn" onClick={onReconnect} disabled={reconnecting}>
            {reconnecting ? "Reconnecting…" : "⟳ Reconnect"}
          </button>
          {/* <a className="nmsp-btn" href={session.url} target="_blank" rel="noopener noreferrer">
            New tab ↗
          </a> */}
        </div>
      </div>

      <div className="nmsp-session">
        {blocked ? (
          <div className="nmsp-state">
            <p>
              Your browser blocks this {label.toLowerCase()} inside the page because it is served over
              plain http.
            </p>
            <a className="nmsp-btn nmsp-btn--terminal" href={session.url} target="_blank" rel="noopener noreferrer">
              Open {label} in a new tab ↗
            </a>
          </div>
        ) : (
          <iframe
            key={session.key}
            src={session.url}
            title={`${title} ${label}`}
            className="nmsp-iframe"
            allow="clipboard-read; clipboard-write; fullscreen"
          />
        )}
      </div>
    </>
  );
};

// ---------------------------------------------------------------
// Devices panel — rendered INSIDE the operator profile modal.
//
// Props:
//   operator      — { phoneNumber, operatorName, username }
//   onBack        — () => void        go back to the profile view
//   onModeChange  — (mode) => void    "devices" | "session", so the
//                                     parent can widen the modal
// ---------------------------------------------------------------
//   syncOnly      — true: only Sync (no Terminal / Web), e.g. the dashboard's Devices
//   backLabel     — text of the back button (default "← Back")
const OperatorDevicesPanel = ({ operator, onBack, onModeChange, syncOnly = false, backLabel = "← Back" }) => {
  const phone = String(operator?.phoneNumber || "").replace(/\D/g, "");

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [opening, setOpening] = useState(null); // { id, kind }
  const [session, setSession] = useState(null); // { device, kind, url, expiresAt, key }

  // Tell the parent which view we're in (it sizes the modal).
  useEffect(() => {
    onModeChange?.(session ? "session" : "devices");
  }, [session, onModeChange]);

  // Escape: session → devices → profile.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (sessionRef.current) setSession(null);
      else onBack?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onBack]);

  const fetchDevices = useCallback(
    async (silent = false) => {
      if (!phone) {
        setError("This account has no phone number, so its devices can't be looked up.");
        setLoading(false);
        return;
      }
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const { data } = await apiClient.get(`${NMS_DIR}/nms_get_devices.php`, {
          params: { operator_phone: phone },
        });
        if (!data?.success) throw new Error(data?.message || "Could not load devices");
        setDevices(Array.isArray(data.devices) ? data.devices : []);
        setError(null);
      } catch (err) {
        if (!silent) setError(apiError(err, "Could not load devices"));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [phone]
  );

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const openSession = async (device, kind) => {
    setOpening({ id: device.device_id, kind });
    try {
      const body = { device_id: device.device_id, operator_phone: phone };
      // Terminal is POST; the NMS web-url endpoint is GET-only, so ours is too.
      const { data } =
        kind === "terminal"
          ? await apiClient.post(`${NMS_DIR}/nms_device_terminal.php`, body)
          : await apiClient.get(`${NMS_DIR}/nms_device_web_url.php`, { params: body });
      if (!data?.success || !data.url) throw new Error(data?.message || "No link returned");

      setSession({
        device,
        kind,
        url: data.url,
        // Count down from our own clock — NMS expires_at timezones differ.
        expiresAt: Date.now() + (Number(data.ttl_seconds) || 300) * 1000,
        key: Date.now(),
      });
    } catch (err) {
      const what = kind === "terminal" ? "terminal" : "web UI";
      toast.error(`${device.device_name || "Device"}: ${apiError(err, `Could not open ${what}`)}`);
    } finally {
      setOpening(null);
    }
  };

  if (session) {
    return (
      <div className="nmsp nmsp--session">
        <SessionView
          session={session}
          onBack={() => setSession(null)}
          onReconnect={() => openSession(session.device, session.kind)}
          reconnecting={!!opening}
        />
      </div>
    );
  }

  return (
    <div className="nmsp">
      <div className="nmsp-bar">
        <button type="button" className="nmsp-back" onClick={onBack}>
          {backLabel}
        </button>
        <div className="nmsp-bar-title">
          <strong>Network devices</strong>
          {!loading && !error && <span className="nmsp-chip">{devices.length}</span>}
        </div>
        <div className="nmsp-bar-tools">
          <button
            type="button"
            className="nmsp-btn"
            onClick={() => fetchDevices()}
            disabled={loading}
            title="Reload the device list"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="nmsp-body">
        {loading && (
          <div className="nmsp-state">
            <span className="nmsp-spinner nmsp-spinner--lg" aria-hidden="true" />
            <p>Loading devices…</p>
          </div>
        )}

        {!loading && error && (
          <div className="nmsp-state nmsp-state--error">
            <p>{error}</p>
            {phone && (
              <button type="button" className="nmsp-btn" onClick={() => fetchDevices()}>
                Try again
              </button>
            )}
          </div>
        )}

        {!loading && !error && devices.length === 0 && (
          <div className="nmsp-state">
            <p>No OLTs or switches are registered for this account on the NMS yet.</p>
          </div>
        )}

        {!loading && !error && devices.length > 0 && (
          <div className="nmsp-list">
            {devices.map((device) => (
              <DeviceCard
                key={device.device_id}
                device={device}
                phone={phone}
                opening={opening}
                onOpenSession={openSession}
                onSynced={() => fetchDevices(true)}
                syncOnly={syncOnly}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatorDevicesPanel;